import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import packageJSON from '../../../../../package.json';
import Button from '../../../../components/Button';
import PageHeader from '../../../../components/PageHeader';
import AppConfig from '../../../../config';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import RequestService from '../../../../services/RequestService';
import TypesService from '../../../../services/TypesService';
import { appSlice, topUpCardSlice } from '../../../../store/slices';

import './TopUpCardConfirm.scss';

const { routes } = AppConfig;

const CREDIT_CURRENCY = packageJSON?.creditCurrency ?? 'Credits';
const BlockchainsById = TypesService.BlockchainsById;
const CurrenciesById = TypesService.CurrenciesById;
const USDT_TRC20_ID = 3;
const CREDIT_CURRENCY_ID = 8;

const TopUpCardConfirm = () => {
	const [card, setCard] = useState({});
	const [asset, setAsset] = useState({});
	const [sourceAccount, setSourceAccount] = useState({});

	const availableCurrencies = useSelector(store => store.app.availableCurrencies);
	const cardsList = useSelector(state => state.card.cardsList);
	const amount = useSelector(state => state.topUpCard.amount);
	const feeData = useSelector(store => store.topUpCard.feeData);
	const withdrawalId = useSelector(store => store.topUpCard.withdrawalId);
	const walletAddresses = useSelector(store => store.topUpCard.walletAddresses);

	const cardAddress = card?.Addresses?.find(address => address.CurrencyId === asset?.Id);
	const anotherCardsAddresses = cardsList
		.map(cardItem => {
			const address = cardItem?.Addresses?.find(address => address.CurrencyId === asset?.Id);
			if (cardItem.Id !== card?.Id && address) return address;
		})
		.filter(Boolean);
	const allSourceAddresses = [...walletAddresses, ...anotherCardsAddresses];

	const currencyData = CurrenciesById[asset?.Id];
	const currencyBlockchainData = BlockchainsById[currencyData?.BlockchainId];

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	useEffect(() => {
		const cardId = Number(searchParams.get('cardId'));
		const card = cardsList.find(card => card.Id === cardId);
		setCard(card);

		const assetId = Number(searchParams.get('assetId'));
		const asset = availableCurrencies.find(currency => currency.Id === assetId);
		setAsset(asset);

		const addressId = Number(searchParams.get('addressId'));
		const sourceAccount = allSourceAddresses?.find(address => address.AddressId === addressId);
		setSourceAccount(sourceAccount);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const confirm = () => {
		const requestData = { Id: withdrawalId };

		const resolveFunc = response => {
			if (response?.Status === 'Success') {
				HelperService.showNotification({
					type: 'success',
					title: t('Successful'),
					descriptin: t('The transaction has been sent.'),
				});
				dispatch(topUpCardSlice.actions.resetState());

				navigate({ pathname: routes.mainPage, hash: card.Id.toString() });
			} else {
				navigate(-1);
				RequestService.BaseThenFunc(response);
			}
			dispatch(appSlice.actions.setPinConfirm());
		};

		if (feeData.CurrencyId === USDT_TRC20_ID) {
			const requestMethod = async data => {
				try {
					const timerId = setTimeout(() => {
						navigate({ pathname: routes.mainPage, hash: card.Id.toString() });
						HelperService.showNotification({
							type: 'success',
							title: t('Your payment is being processed.'),
							descriptin: t(
								'Processing may take some time and involve multiple technical transactions, all of which will be recorded in the transaction history.'
							),
						});
					}, 3000);

					const response = await RequestService.WithdrawalConfirm(data);
					clearTimeout(timerId);
					return response;
				} catch (error) {
					navigate(-1);
					RequestService.BaseErrorFunc(error);
				}
			};

			HelperService.confirmPin({
				requestMethod,
				requestData,
				resolveFunc,
				rejectFunt: () => {},
			});
		} else {
			HelperService.confirmPin({
				requestMethod: RequestService.WithdrawalConfirm,
				requestData,
				resolveFunc,
			});
		}
	};

	return (
		<div className='TopUpCardConfirm'>
			<PageHeader
				header={'Confirm transaction'}
				icon={currencyData?.Name}
				iconLabel={currencyData?.CompanyIcon !== currencyBlockchainData?.Icon ? currencyBlockchainData?.Icon : null}
			/>
			<div className='TopUpCardConfirm__body'>
				<div>
					<div className='TopUpCardConfirm__amount-block FormBlock'>
						<div className='TopUpCardConfirm__amount'>
							<div className='TopUpCardConfirm__amount-label'>{t('Amount to up')}</div>
							<div className='TopUpCardConfirm__amount-value'>
								{HelperService.FormatCrypto(amount, currencyData?.Precision, 'floor')} {currencyData?.Name}
							</div>
						</div>
						<div className='TopUpCardConfirm__fee'>
							<div className='TopUpCardConfirm__fee-label'>{t('Network fee_v2')}</div>
							<div className='TopUpCardConfirm__fee-value'>
								{HelperService.FormatCrypto(feeData.Fee, CurrenciesById[feeData.CurrencyId].Precision)}{' '}
								{feeData.CurrencyId === CREDIT_CURRENCY_ID ? CREDIT_CURRENCY : CurrenciesById[feeData.CurrencyId].Name}
							</div>
						</div>
					</div>
					<div className='TopUpCardConfirm__addresses-block FormBlock'>
						<div className='TopUpCardConfirm__addresses-from'>
							<div className='TopUpCardConfirm__from-label'>{t('From')}</div>
							<div className='TopUpCardConfirm__from-address'>{sourceAccount?.Address}</div>
						</div>
						<div className='TopUpCardConfirm__addresses-to'>
							<div className='TopUpCardConfirm__to-label'>{t('To')}</div>
							<div className='TopUpCardConfirm__to-address'>{cardAddress?.Address}</div>
						</div>
						<div className='TopUpCardConfirm__addresses-network'>
							<div className='TopUpCardConfirm__network-label'>{t('Network')}</div>
							<div className='TopUpCardConfirm__network-name'>{`${currencyBlockchainData?.Name} (${currencyData?.Standart})`}</div>
						</div>
					</div>
				</div>
				<Button className='TopUpCardConfirm__confirm-button' onClick={confirm} iconBefore={'VIEWFINDER_KEY'}>
					{t('Confirm')}
				</Button>
			</div>
		</div>
	);
};

export default TopUpCardConfirm;
